import React from 'react';
import { IStorefrontLink, IStorefrontSettings, IStorefrontTheme, IGlobalStorefrontSettings } from '../../../../models/Storefront';
import { Callout, DirectionalHint, IconButton, TextField, Toggle, TooltipHost } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import RichTextField from '../../../Common/RichTextField';
import MarkdownView from 'react-showdown';
import StorefrontTextEditor from '../StorefrontTextEditor';
import StorefrontFooterLogoEditor from './StorefrontFooterLogoEditor';
import {
    useDeleteStorefrontFooterLinkMutation,
    useSaveStorefrontFooterLinkMutation,
    useSaveStorefrontFooterLinksMutation
} from '../../../../store/endpoints/StorefrontLinkEndpoints';
import { resolvedStyleValue } from '../styleConfig';
import { elementStyleFrom } from '../styleControls';
import { parseFooterConfig } from '../headerFooterConfig';
import { localizerFor } from '../localizationConfig';
import { readableOn } from '../colorUtils';
import { DynamicSetting } from '../../../Common/Setting';
import ConditionalSetting from '../../../Common/ConditionalSetting';

const FacebookIcon: React.FC = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
);

const YouTubeIcon: React.FC = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
        <path d="M23.495 6.205a3.007 3.007 0 0 0-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 0 0 .527 6.205a31.247 31.247 0 0 0-.522 5.805 31.247 31.247 0 0 0 .522 5.783 3.007 3.007 0 0 0 2.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 0 0 2.088-2.088 31.247 31.247 0 0 0 .5-5.783 31.247 31.247 0 0 0-.5-5.805zM9.609 15.601V8.408l6.264 3.602z"/>
    </svg>
);

const LinkedInIcon: React.FC = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
);

const TwitterIcon: React.FC = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
);

const BlueskyIcon: React.FC = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
        <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 0 1-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8z"/>
    </svg>
);

type SocialField = 'footerFacebook' | 'footerYoutube' | 'footerLinkedIn' | 'footerTwitter' | 'footerBluesky' | 'footerX';

interface ISocialConfig {
    field: SocialField;
    /** Names the setting id (storefront.footer.social.<key>) — distinct from `label`, which is display copy. */
    key: string;
    label: string;
    Icon: React.FC;
    prefix: string;
    urlBase: string;
    imageUrl: string; // full-color PNG used by the live storefront footer
    scale: number;    // matching per-icon scale from the live storefront footer
}

// imageUrl/scale mirror the live storefront footer (ac-storefront-web Footer.tsx) so
// the non-edit preview shows the same full-color icons shoppers see.
const SOCIALS_BASE = 'https://acapp1stousee0101.blob.core.windows.net/app-images/socials';
const SOCIAL_CONFIGS: ISocialConfig[] = [
    { field: 'footerFacebook',  key: 'facebook', label: 'Facebook',    Icon: FacebookIcon,  prefix: 'facebook.com/',     urlBase: 'https://facebook.com/',      imageUrl: `${SOCIALS_BASE}/facebook.png`, scale: 0.65 },
    { field: 'footerTwitter',   key: 'twitter',  label: 'Twitter',     Icon: TwitterIcon,   prefix: 'twitter.com/',      urlBase: 'https://twitter.com/',       imageUrl: `${SOCIALS_BASE}/x.png`,        scale: 0.55 },
    { field: 'footerX',        key: 'x',         label: 'X',           Icon: TwitterIcon,   prefix: 'x.com/',            urlBase: 'https://x.com/',             imageUrl: `${SOCIALS_BASE}/x.png`,        scale: 0.55 },
    { field: 'footerLinkedIn',  key: 'linkedin', label: 'LinkedIn',    Icon: LinkedInIcon,  prefix: 'linkedin.com/in/',  urlBase: 'https://linkedin.com/in/',   imageUrl: `${SOCIALS_BASE}/linkedin.png`, scale: 0.65 },
    { field: 'footerYoutube',   key: 'youtube',  label: 'YouTube',     Icon: YouTubeIcon,   prefix: 'youtube.com/',      urlBase: 'https://youtube.com/',       imageUrl: `${SOCIALS_BASE}/youtube.png`,  scale: 1 },
    { field: 'footerBluesky',   key: 'bluesky',  label: 'Bluesky',     Icon: BlueskyIcon,   prefix: 'bsky.app/profile/', urlBase: 'https://bsky.app/profile/',  imageUrl: `${SOCIALS_BASE}/bluesky.png`,  scale: 1 },
];

// Resolve whatever the merchant entered to the best link we can — mirrors the server-side
// MergeUrlIfValue (ac-main StorefrontSettings.ToShared). A full URL (e.g. a LinkedIn company
// page) is used as-is; a scheme-less domain gets https://; anything else is a handle under the
// platform base. Keep this in sync with the C# resolver so the admin preview matches the shopper.
export const resolveSocialUrl = (urlBase: string, value?: string): string => {
    const v = (value ?? '').trim();
    if (!v) return '';
    if (/^https?:\/\//i.test(v)) return v;
    if (/^www\./i.test(v)) return `https://${v}`;
    return `${urlBase}${v.replace(/^\/+/, '')}`;
};

interface ISocialIconButtonProps {
    config: ISocialConfig;
    handle?: string;
    editMode?: boolean;
    onChange: (handle: string) => void;
}

const SocialIconButton: React.FC<ISocialIconButtonProps> = ({ config, handle, editMode, onChange }) => {
    const [calloutOpen, setCalloutOpen] = React.useState(false);
    const [input, setInput] = React.useState(handle ?? '');
    const buttonId = useId('social-btn');

    React.useEffect(() => {
        setInput(handle ?? '');
    }, [handle]);

    if (!editMode) {
        if (!handle) return null;
        return (
            <a
                href={resolveSocialUrl(config.urlBase, handle)}
                target="_blank"
                rel="noopener noreferrer"
                title={config.label}
                style={{ display: 'flex', alignItems: 'center' }}
            >
                <img
                    src={config.imageUrl}
                    alt={config.label}
                    style={{ width: 48, height: 48, cursor: 'pointer', transform: `scale(${config.scale})` }}
                />
            </a>
        );
    }

    return (
        <>
            <button
                id={buttonId}
                onClick={() => setCalloutOpen(true)}
                title={handle ? `Edit ${config.label}` : `Set ${config.label}`}
                style={{
                    background: 'none',
                    border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: 6,
                    padding: '4px 6px',
                    cursor: 'pointer',
                    color: handle ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <config.Icon />
            </button>
            {calloutOpen && (
                <Callout
                    target={`#${buttonId}`}
                    onDismiss={() => { setInput(handle ?? ''); setCalloutOpen(false); }}
                    setInitialFocus
                    style={{ padding: '1em', minWidth: 300 }}
                    directionalHint={DirectionalHint.topCenter}
                >
                    <DynamicSetting id={`storefront.footer.social.${config.key}`}>
                    <TextField
                        label={config.label}
                        // Show the base as an inline prefix for the common handle case, but drop it
                        // once the merchant pastes a full URL (e.g. a LinkedIn company page) — both
                        // are accepted and resolved by resolveSocialUrl / the server MergeUrlIfValue.
                        prefix={/^(https?:\/\/|www\.)/i.test(input) ? undefined : config.prefix}
                        value={input}
                        onChange={(_, v) => setInput(v ?? '')}
                        onKeyDown={(e: React.KeyboardEvent) => {
                            if (e.key === 'Enter') { onChange(input); setCalloutOpen(false); }
                            if (e.key === 'Escape') { setInput(handle ?? ''); setCalloutOpen(false); }
                        }}
                        onBlur={() => { onChange(input); setCalloutOpen(false); }}
                        placeholder={`${config.prefix}handle — or paste a full URL`}
                    />
                    </DynamicSetting>
                </Callout>
            )}
        </>
    );
};

// --- Footer link item (editable chip in center column) ---

interface IFooterLinkItemProps {
    link: IStorefrontLink;
    sfIndex: number;
    isStorefront: boolean;
    editMode: boolean;
    storefront: string;
    onDragStart: (e: React.DragEvent, index: number) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent, index: number) => void;
}

const FooterLinkItem: React.FC<IFooterLinkItemProps> = ({ link, sfIndex, isStorefront, editMode, storefront, onDragStart, onDragOver, onDrop }) => {
    const [calloutOpen, setCalloutOpen] = React.useState(false);
    const [editText, setEditText] = React.useState(link.text ?? '');
    const [editUrl, setEditUrl] = React.useState(link.url ?? '');
    const [editSeparator, setEditSeparator] = React.useState(link.isSeparator ?? false);
    const btnId = useId('footer-link-item');
    const [saveLink] = useSaveStorefrontFooterLinkMutation();
    const [deleteLink] = useDeleteStorefrontFooterLinkMutation();

    React.useEffect(() => {
        if (!calloutOpen) {
            setEditText(link.text ?? '');
            setEditUrl(link.url ?? '');
            setEditSeparator(link.isSeparator ?? false);
        }
    }, [link.text, link.url, link.isSeparator, calloutOpen]);

    const commitSave = () => {
        saveLink({
            link: { ...link, text: editText, url: editUrl, isSeparator: editSeparator },
            storefront,
        });
    };

    if (!editMode) {
        if (link.disabled) return null;
        if (link.isSeparator) {
            return <h3 style={{ margin: '0 0 8px 0', fontSize: 14, fontWeight: 'bold', color: '#fff' }}>{link.text}</h3>;
        }
        return (
            <a href={link.url} style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: 13, display: 'block' }} onClick={(e) => e.preventDefault()}>
                {link.text}
            </a>
        );
    }

    return (
        <>
            <div
                draggable={isStorefront}
                onDragStart={isStorefront ? (e) => onDragStart(e, sfIndex) : undefined}
                onDragOver={isStorefront ? onDragOver : undefined}
                onDrop={isStorefront ? (e) => onDrop(e, sfIndex) : undefined}
                style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}
            >
                <button
                    id={btnId}
                    onClick={() => { if (isStorefront) setCalloutOpen(true); }}
                    className='storefront-footer-link-chip'
                    style={{
                        opacity: link.disabled ? 0.5 : 1,
                        fontWeight: link.isSeparator ? 'bold' : 'normal',
                        fontSize: link.isSeparator ? 14 : 13,
                        cursor: isStorefront ? 'pointer' : 'default',
                        borderColor: isStorefront ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.15)',
                    }}
                    title={isStorefront ? 'Click to edit' : 'Global link — edit in global settings'}
                >
                    {link.text || '(empty)'}
                </button>
                {isStorefront && (
                    <span className='storefront-footer-link-drag-hint' title='Drag to reorder'>⠿</span>
                )}
            </div>
            {calloutOpen && (
                <Callout
                    target={`#${btnId}`}
                    onDismiss={() => { commitSave(); setCalloutOpen(false); }}
                    setInitialFocus
                    directionalHint={DirectionalHint.topCenter}
                    style={{ padding: '1em', minWidth: 280 }}
                >
                    <DynamicSetting id={`storefront.footerLink.${link.id ?? sfIndex}.isSeparator`}>
                    <Toggle
                        label='Section Header'
                        inlineLabel
                        checked={editSeparator}
                        onChange={(_, v) => setEditSeparator(v ?? false)}
                    />
                    </DynamicSetting>
                    <DynamicSetting id={`storefront.footerLink.${link.id ?? sfIndex}.text`}>
                    <TextField
                        label='Text'
                        value={editText}
                        onChange={(_, v) => setEditText(v ?? '')}
                        style={{ marginTop: '0.5em' }}
                    />
                    </DynamicSetting>
                    <ConditionalSetting when={!editSeparator} idPrefix={`storefront.footerLink.${link.id ?? sfIndex}.url`}>
                        <DynamicSetting id={`storefront.footerLink.${link.id ?? sfIndex}.url`}>
                        <TextField
                            label='Hyperlink'
                            value={editUrl}
                            onChange={(_, v) => setEditUrl(v ?? '')}
                            style={{ marginTop: '0.5em' }}
                        />
                        </DynamicSetting>
                    </ConditionalSetting>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5em' }}>
                        <IconButton
                            iconProps={{ iconName: 'Delete' }}
                            title='Delete link'
                            onClick={() => {
                                if (link.id) deleteLink({ storefront, id: link.id });
                                setCalloutOpen(false);
                            }}
                        />
                    </div>
                </Callout>
            )}
        </>
    );
};

// --- Footer center links column ---

interface IFooterLinksCenterProps {
    storefrontLinks: IStorefrontLink[];
    globalLinks: IStorefrontLink[];
    storefront: string;
    editMode?: boolean;
    ignoreGlobalLinks?: boolean;
    textColor?: string;
    linkHeaderStyle?: React.CSSProperties;
    linkStyle?: React.CSSProperties;
}

type LinkGroup = { header: IStorefrontLink | null; links: IStorefrontLink[] };

function groupBySection(links: IStorefrontLink[]): LinkGroup[] {
    const groups: LinkGroup[] = [];
    let current: LinkGroup = { header: null, links: [] };
    for (const link of links) {
        if (link.isSeparator) {
            if (current.links.length > 0 || current.header) groups.push(current);
            current = { header: link, links: [] };
        } else {
            current.links.push(link);
        }
    }
    if (current.links.length > 0 || current.header) groups.push(current);
    return groups;
}

const FooterLinksCenter: React.FC<IFooterLinksCenterProps> = ({ storefrontLinks, globalLinks, storefront, editMode, ignoreGlobalLinks, textColor, linkHeaderStyle, linkStyle }) => {
    const [saveLink] = useSaveStorefrontFooterLinkMutation();
    const [saveLinks] = useSaveStorefrontFooterLinksMutation();

    const effectiveGlobal = ignoreGlobalLinks ? [] : globalLinks.filter(l => editMode || !l.disabled);
    const allLinks = [...effectiveGlobal, ...storefrontLinks.filter(l => editMode || !l.disabled)];

    const handleDragStart = (e: React.DragEvent, index: number) => {
        e.dataTransfer.setData('text/plain', String(index));
    };
    const handleDragOver = (e: React.DragEvent) => e.preventDefault();
    const handleDrop = (e: React.DragEvent, targetIndex: number) => {
        const sourceIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
        if (isNaN(sourceIndex) || sourceIndex === targetIndex) return;
        const links = [...storefrontLinks];
        const [moved] = links.splice(sourceIndex, 1);
        links.splice(targetIndex, 0, moved);
        saveLinks({ links: links.map((l, i) => ({ ...l, sortOrder: i + 1 })), storefront });
    };

    const handleAdd = () => {
        saveLink({
            link: {
                text: 'New Link',
                url: '',
                sortOrder: storefrontLinks.length + 1,
                storefront,
                type: 'footer',
                disabled: false,
                shared: false,
                isSeparator: false,
            },
            storefront,
        });
    };

    if (!editMode) {
        const groups = groupBySection(allLinks);
        if (groups.length === 0) return null;
        return (
            <>
                {groups.map((group, gi) => (
                    <div key={gi} className='storefront-footer-link-group'>
                        {group.header && (
                            <h3 style={{ margin: '0 0 8px 0', fontSize: 14, fontWeight: 'bold', color: textColor ?? '#fff', ...linkHeaderStyle }}>
                                {group.header.text}
                            </h3>
                        )}
                        {group.links.map((link, li) => (
                            <a
                                key={link.id ?? li}
                                href={link.url}
                                style={{ color: textColor ?? 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: 13, display: 'block', marginBottom: 4, ...linkStyle }}
                                onClick={(e) => e.preventDefault()}
                            >
                                {link.text}
                            </a>
                        ))}
                    </div>
                ))}
            </>
        );
    }

    // Edit mode: flat list with drag-and-drop
    // Global links first (not draggable), then storefront links (draggable)
    return (
        <div className='storefront-footer-links-edit'>
            {effectiveGlobal.map((link, idx) => (
                <FooterLinkItem
                    key={`g-${link.id ?? idx}`}
                    link={link}
                    sfIndex={-1}
                    isStorefront={false}
                    editMode={true}
                    storefront={storefront}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                />
            ))}
            {storefrontLinks.map((link, idx) => (
                <FooterLinkItem
                    key={`sf-${link.id ?? idx}`}
                    link={link}
                    sfIndex={idx}
                    isStorefront={true}
                    editMode={true}
                    storefront={storefront}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                />
            ))}
            <button className='storefront-footer-add-link-btn' onClick={handleAdd} title='Add footer link'>
                + Add Link
            </button>
        </div>
    );
};

// --- Main footer editor ---

interface IStorefrontFooterEditorProps {
    storefrontSettings: IStorefrontSettings;
    storefrontTheme?: IStorefrontTheme;
    globalSettings: IGlobalStorefrontSettings;
    globalTheme?: IStorefrontTheme;
    editMode?: boolean;
    onStorefrontSettingsChange: (newSettings: IStorefrontSettings) => void;
    footerLinks?: IStorefrontLink[];
    globalFooterLinks?: IStorefrontLink[];
    // When provided (edit mode), a hover-reveal pencil at the top-right of the footer preview opens
    // the footer layout panel.
    onEditLayout?: () => void;
}

const StorefrontFooterEditor: React.FC<IStorefrontFooterEditorProps> = (props) => {
    const [calloutOpen, setCalloutOpen] = React.useState(false);
    const [hoverLayout, setHoverLayout] = React.useState(false);
    const editButtonId = useId('storefront-footer-edit-button');
    // Shopper-facing labels come from the storefront's own localization overrides, exactly as the
    // live footer resolves them. Built here rather than passed in, so every surface that renders
    // this editor (the Home tab and the Header & Footer tab both do) is localized without each
    // caller having to remember to thread it.
    const L = React.useMemo(
        () => localizerFor(props.storefrontSettings.localizationConfig),
        [props.storefrontSettings.localizationConfig]);

    if (props.storefrontSettings.includeFooter !== true && !props.editMode) {
        return null;
    }

    const handleSocialChange = (field: SocialField, handle: string) => {
        props.onStorefrontSettingsChange({ ...props.storefrontSettings, [field]: handle });
    };

    const anySocialSet = SOCIAL_CONFIGS.some(
        (c) => props.storefrontSettings[c.field] ?? (props.globalSettings as unknown as IStorefrontSettings)[c.field]
    );

    const storefront = props.storefrontSettings.id ?? '';
    const footerLinks = props.footerLinks ?? [];
    const globalFooterLinks = props.globalFooterLinks ?? [];
    const showFooterLinks = footerLinks.length > 0 || globalFooterLinks.length > 0 || props.editMode;

    // The real storefront footer uses the same colors as the header bar (menuBarColor
    // / menuBarTextColor). Match that here so the preview is accurate — same fallback
    // chain as StorefrontHeaderEditor.
    const menuBarColor = resolvedStyleValue(props.storefrontSettings, 'menuBarColor') || props.storefrontTheme?.menuBarColor || props.globalSettings.menuBarColor || props.globalTheme?.menuBarColor || '#fff';
    const menuBarTextColor = resolvedStyleValue(props.storefrontSettings, 'menuBarTextColor') || props.storefrontTheme?.menuBarTextColor || props.globalSettings.menuBarTextColor || props.globalTheme?.menuBarTextColor || '#000';
    // The live footer uses the header font and is a tall (440px) band; match both.
    const headerFont = resolvedStyleValue(props.storefrontSettings, 'headerFont') || props.globalSettings.headerFont || undefined;

    const footerConfig = parseFooterConfig(props.storefrontSettings.footerConfig);
    const footerBarStyle = elementStyleFrom(footerConfig.bar);
    const footerLeftStyle = elementStyleFrom(footerConfig.leftText);
    const footerRightStyle = elementStyleFrom(footerConfig.rightText);
    const footerLinkHeaderStyle = elementStyleFrom(footerConfig.linkHeader);
    const footerLinkStyle = elementStyleFrom(footerConfig.link);
    // Match the storefront: footer links default to a readable color derived from the footer bar's
    // effective background (footerConfig.bar wins over menuBarColor), keeping menuBarTextColor when
    // it already contrasts. Prevents the preview showing an invisible link the storefront now fixes.
    const footerBarBg = (footerBarStyle.backgroundColor as string | undefined) ?? menuBarColor;
    const footerTextColor = readableOn(footerBarBg, menuBarTextColor);

    return (
        <div className='storefront-footer'>
            <div
                className='storefront-footer-content'
                style={{ backgroundColor: menuBarColor, color: menuBarTextColor, fontFamily: headerFont, minHeight: 440, ...footerBarStyle }}
                onMouseEnter={() => setHoverLayout(true)}
                onMouseLeave={() => setHoverLayout(false)}
            >
                <div className='storefront-footer-left' style={footerLeftStyle}>
                    <StorefrontFooterLogoEditor
                        storefrontSettings={props.storefrontSettings}
                        globalSettings={props.globalSettings}
                        onStorefrontSettingsChange={props.onStorefrontSettingsChange}
                        editMode={props.editMode}
                    />
                    {props.editMode ? (
                        <div className='storefront-editable-area' style={{ padding: '4px 8px' }}>
                            <RichTextField
                                settingId='storefront.footer.text'
                                value={props.storefrontSettings.footerText}
                                onChange={(text) => props.onStorefrontSettingsChange({ ...props.storefrontSettings, footerText: text })}
                                placeholder='Enter footer text here...'
                                previewStyle={{ backgroundColor: 'transparent', height: 'auto', minHeight: 'unset' }}
                            />
                        </div>
                    ) : (
                        <MarkdownView markdown={props.storefrontSettings.footerText || ''} />
                    )}
                </div>
                <div className='storefront-footer-center'>
                    {showFooterLinks && (
                        <FooterLinksCenter
                            storefrontLinks={footerLinks}
                            globalLinks={globalFooterLinks}
                            storefront={storefront}
                            editMode={props.editMode}
                            ignoreGlobalLinks={props.storefrontSettings.ignoreGlobalLinks}
                            textColor={footerTextColor}
                            linkHeaderStyle={footerLinkHeaderStyle}
                            linkStyle={footerLinkStyle}
                        />
                    )}
                </div>
                <div className='storefront-footer-right' style={footerRightStyle}>
                    <h2>{L('localization_footer_contact_heading')}</h2>
                    {props.editMode ? (
                        <div className='storefront-editable-area' style={{ padding: '4px 8px' }}>
                            <RichTextField
                                settingId='storefront.footer.contactDetails'
                                value={props.storefrontSettings.footerContactDetails}
                                onChange={(text) => props.onStorefrontSettingsChange({ ...props.storefrontSettings, footerContactDetails: text })}
                                placeholder='Enter contact details here...'
                                previewStyle={{ backgroundColor: 'transparent', height: 'auto', minHeight: 'unset' }}
                            />
                        </div>
                    ) : (
                        <MarkdownView markdown={props.storefrontSettings.footerContactDetails || ''} />
                    )}
                    <StorefrontTextEditor
                        value={props.storefrontSettings.footerEmail ?? props.globalSettings.footerEmail}
                        onChange={(text) => props.onStorefrontSettingsChange({ ...props.storefrontSettings, footerEmail: text })}
                        notSetValue='Email Address'
                        style={{ marginTop: '1em' }}
                        editable={props.editMode}
                    />
                    <StorefrontTextEditor
                        value={props.storefrontSettings.footerPhone ?? props.globalSettings.footerPhone}
                        onChange={(text) => props.onStorefrontSettingsChange({ ...props.storefrontSettings, footerPhone: text })}
                        notSetValue='Phone Number'
                        style={{ marginTop: '0.5em', fontSize: '28px', fontWeight: 'bold' }}
                        editable={props.editMode}
                    />
                </div>
                {/* Socials anchored bottom-right, matching the live storefront footer. */}
                {(props.editMode || anySocialSet) && (
                    <div
                        className='storefront-footer-social-links'
                        style={{ position: 'absolute', right: 24, bottom: 24, marginTop: 0, gap: 5 }}
                    >
                        {SOCIAL_CONFIGS.map((config) => (
                            <SocialIconButton
                                key={config.field}
                                config={config}
                                handle={props.storefrontSettings[config.field] ?? (props.globalSettings as unknown as IStorefrontSettings)[config.field]}
                                editMode={props.editMode}
                                onChange={(handle) => handleSocialChange(config.field, handle)}
                            />
                        ))}
                    </div>
                )}
                {props.editMode && props.onEditLayout && (
                    <TooltipHost content='Edit footer layout' styles={{ root: { position: 'absolute', top: '5px', right: '44px', zIndex: 11 } }}>
                        <IconButton
                            iconProps={{ iconName: 'Edit' }}
                            ariaLabel='Edit footer layout'
                            onClick={props.onEditLayout}
                            styles={{ root: { opacity: hoverLayout ? 1 : 0, transition: 'opacity .15s', background: 'rgba(255,255,255,0.92)', color: '#323130', borderRadius: 4, boxShadow: '0 1px 3px rgba(0,0,0,0.25)' } }}
                        />
                    </TooltipHost>
                )}
                {props.editMode && (
                    <IconButton
                        id={editButtonId}
                        iconProps={{ iconName: 'Settings' }}
                        title='Footer settings'
                        onClick={() => setCalloutOpen(true)}
                        style={{ position: 'absolute', top: '5px', right: '5px' }}
                    />
                )}
                {calloutOpen && (
                    <Callout
                        target={`#${editButtonId}`}
                        onDismiss={() => setCalloutOpen(false)}
                        setInitialFocus
                        style={{ padding: '1em' }}
                        directionalHint={DirectionalHint.topRightEdge}
                    >
                        <h3>Footer Settings</h3>
                        <Toggle
                            label='Footer Enabled'
                            inlineLabel
                            onText='On'
                            offText='Off'
                            checked={props.storefrontSettings.includeFooter}
                            onChange={(_, checked) => props.onStorefrontSettingsChange({ ...props.storefrontSettings, includeFooter: checked ?? false })}
                        />
                    </Callout>
                )}
            </div>
        </div>
    );
};

export default StorefrontFooterEditor;
